import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, resource, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged, lastValueFrom } from 'rxjs';
import { EcabInputComponent, EcabSelectComponent, EcabButtonComponent } from '@ecab/ecab-ui';
import { MarkdownEditorComponent } from '../../shared/markdown-editor/markdown-editor.component';
import {
  ApiClient,
  CollaboratorDto,
  CreateMessageDto,
  CreateMessageDtoCaseType,
  CreateMessageDtoChannel,
  CreateMessageDtoDirection,
  CreateMessageDtoQuestionType,
  MessageDto,
  UpdateMessageCollaboratorDto,
  IMyEcabAddressDto,
  IMyEcabBuildingSummaryDto,
  IMyEcabPersonDto,
  MyEcabIncidentSummaryDto
} from '../../services/api.clients.generated';
import { BackofficeMessageUploadService } from '../../services/backoffice-message-upload.service';
import { IcareService } from '../../services/icare.service';
import { NotificationService } from '../../notifications/notification.service';
import {
  formatPersonDisplay,
  getPersonLocality,
  getPersonPrimaryLabel,
  PERSON_SEARCH_DEBOUNCE_MS,
  sortPersonOptions
} from '../person-picker.utils';

interface PendingAttachment {
  id: string;
  file: File;
  filename: string;
  contentType: string;
  size: number;
}

@Component({
  selector: 'app-new-message-modal',
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormFieldModule,
    MatIconButton,
    MatInput,
    MatOption,
    TranslatePipe,
    EcabInputComponent,
    EcabSelectComponent,
    EcabButtonComponent,
    MarkdownEditorComponent
  ],
  templateUrl: './new-message-modal.component.html',
  styleUrl: './new-message-modal.component.scss'
})
export class NewMessageModalComponent {
  private static readonly duplicateSeparator = '::';

  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() created = new EventEmitter<MessageDto>();

  readonly CreateMessageDtoDirection = CreateMessageDtoDirection;
  readonly CreateMessageDtoChannel = CreateMessageDtoChannel;
  readonly CreateMessageDtoQuestionType = CreateMessageDtoQuestionType;

  submitting = signal(false);

  personSearchTerm = signal<string | IMyEcabPersonDto | null>(null);
  personSearch$ = new Subject<string>();
  debouncedPersonSearchTerm = toSignal(
    this.personSearch$.pipe(
      debounceTime(PERSON_SEARCH_DEBOUNCE_MS),
      distinctUntilChanged()
    ),
    { initialValue: '' }
  );
  collaboratorSearchTerm = signal<string>('');
  selectedPerson = signal<IMyEcabPersonDto | null>(null);
  selectedBuilding = signal<IMyEcabBuildingSummaryDto | null>(null);
  selectedCase = signal<MyEcabIncidentSummaryDto | null>(null);
  selectedCollaborator = signal<CollaboratorDto | null>(null);
  selectedAttachments = signal<PendingAttachment[]>([]);

  form!: FormGroup;

  personsResource = resource<IMyEcabPersonDto[], string>({
    params: () => {
      const searchTerm = this.debouncedPersonSearchTerm();
      return typeof searchTerm === 'string' ? searchTerm : '';
    },
    loader: async ({ params }) => {
      if (!params) return [];
      const response = await lastValueFrom(this.icareService.getPersons(params));
      return response.content ?? [];
    }
  });

  personsOptions = computed(() => {
    if (this.personsResource.hasValue()) {
      return sortPersonOptions(this.personsResource.value());
    }
    return undefined;
  });

  buildingResource = resource<IMyEcabBuildingSummaryDto[], number | undefined>({
    params: () => this.selectedPerson()?.id ?? undefined,
    loader: async ({ params }) => {
      if (!params) return [];
      const response = await lastValueFrom(this.icareService.getBuildings(params.toString()));
      return response.content ?? [];
    }
  });

  buildingOptions = computed(() => {
    if (this.buildingResource.hasValue()) {
      return this.buildingResource.value();
    }
    return undefined;
  });

  caseResource = resource<MyEcabIncidentSummaryDto[], { personId?: number; buildingId?: number }>({
    params: () => ({
      personId: this.selectedPerson()?.id ?? undefined,
      buildingId: this.selectedBuilding()?.id ?? undefined
    }),
    loader: async ({ params }) => {
      if (!params.personId || !params.buildingId) return [];
      const response = await lastValueFrom(
        this.icareService.getCases(params.personId.toString(), params.buildingId.toString())
      );
      return response.content ?? [];
    }
  });

  caseOptions = computed(() => {
    if (this.caseResource.hasValue()) {
      return this.caseResource.value();
    }
    return undefined;
  });

  collaboratorsResource = resource<CollaboratorDto[], undefined>({
    loader: async () => {
      return await lastValueFrom(this.messageService.getCollaborators());
    }
  });

  collaboratorOptions = computed(() => {
    if (this.collaboratorsResource.hasValue()) {
      return this.collaboratorsResource.value();
    }
    return undefined;
  });

  filteredCollaboratorOptions = computed(() => {
    const options = this.collaboratorOptions() || [];
    const searchTerm = this.collaboratorSearchTerm().toLowerCase();
    if (!searchTerm) return options;
    return options.filter(collab =>
      `${collab.firstname} ${collab.lastname}`.toLowerCase().includes(searchTerm) ||
      (collab.email?.toLowerCase().includes(searchTerm) ?? false)
    );
  });

  constructor(
    private readonly messageService: ApiClient,
    private readonly messageUploadService: BackofficeMessageUploadService,
    private readonly icareService: IcareService,
    private readonly fb: FormBuilder,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService
  ) {
    this.form = this.fb.group({
      subject: ['', Validators.required],
      content: ['', Validators.required],
      direction: [CreateMessageDtoDirection.OUT, Validators.required],
      channel: [CreateMessageDtoChannel.PORTAL, Validators.required],
      language: [this.translateService.currentLang || 'fr', Validators.required],
      questionType: [CreateMessageDtoQuestionType.GENERAL_QUESTION, Validators.required]
    });
  }

  onClose() {
    this.resetForm();
    this.closed.emit();
  }

  resetForm() {
    this.form.reset({
      subject: '',
      content: '',
      direction: CreateMessageDtoDirection.OUT,
      channel: CreateMessageDtoChannel.PORTAL,
      language: this.translateService.currentLang || 'fr',
      questionType: CreateMessageDtoQuestionType.GENERAL_QUESTION
    });
    this.personSearchTerm.set(null);
    this.collaboratorSearchTerm.set('');
    this.selectedPerson.set(null);
    this.selectedBuilding.set(null);
    this.selectedCase.set(null);
    this.selectedCollaborator.set(null);
    this.selectedAttachments.set([]);
  }

  onPersonSelected(person: IMyEcabPersonDto) {
    this.selectedPerson.set(person);
    this.selectedBuilding.set(null);
    this.selectedCase.set(null);
    this.personSearchTerm.set(person);
  }

  clearPerson() {
    this.selectedPerson.set(null);
    this.selectedBuilding.set(null);
    this.selectedCase.set(null);
    this.personSearchTerm.set(null);
  }

  onPersonSearchChange(value: string | IMyEcabPersonDto | null) {
    if (typeof value === 'string') {
      const currentSelection = this.selectedPerson();
      if (currentSelection && this.displayPerson(currentSelection) !== value) {
        this.selectedPerson.set(null);
        this.selectedBuilding.set(null);
        this.selectedCase.set(null);
      }

      if (!currentSelection || this.displayPerson(currentSelection) !== value) {
        this.personSearch$.next(value);
      }

      this.personSearchTerm.set(value);
      return;
    }

    if (value) {
      this.selectedPerson.set(value);
      this.personSearchTerm.set(value);
      return;
    }

    this.clearPerson();
  }

  displayPerson(person: IMyEcabPersonDto | string | null): string {
    return formatPersonDisplay(person);
  }

  getPersonPrimaryLabel(person: IMyEcabPersonDto | null | undefined): string {
    return getPersonPrimaryLabel(person);
  }

  getPersonLocality(person: IMyEcabPersonDto | null | undefined): string {
    return getPersonLocality(person);
  }

  onBuildingSelected(building: IMyEcabBuildingSummaryDto | null) {
    this.selectedBuilding.set(building);
    this.selectedCase.set(null);
  }

  onCaseSelected(caseSelected: MyEcabIncidentSummaryDto | null) {
    this.selectedCase.set(caseSelected ?? null);
  }

  onCollaboratorSelected(collaborator: CollaboratorDto) {
    this.selectedCollaborator.set(collaborator);
    this.collaboratorSearchTerm.set(`${collaborator.firstname} ${collaborator.lastname}`.trim());
  }

  clearCollaborator() {
    this.selectedCollaborator.set(null);
    this.collaboratorSearchTerm.set('');
  }

  displayCollaborator(collaborator: CollaboratorDto | null): string {
    return collaborator ? `${collaborator.firstname} ${collaborator.lastname}` : '';
  }

  async submit() {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    try {
      const formValue = this.form.getRawValue();
      const person = this.selectedPerson();
      const building = this.selectedBuilding();
      const caseSelected = this.selectedCase();

      const dto = new CreateMessageDto({
        personId: person?.id?.toString() ?? '',
        companyName: person?.companyName ?? '',
        firstname: person?.firstName ?? '',
        lastname: person?.lastName ?? '',
        email: this.getPersonEmail(person),
        phone: this.getPersonPhone(person),
        direction: formValue.direction ?? CreateMessageDtoDirection.OUT,
        channel: formValue.channel ?? CreateMessageDtoChannel.PORTAL,
        buildingSource: building ? 'ICARE' : '',
        buildingAddress: building?.address ? this.formatAddress(building.address) : '',
        buildingId: building?.id?.toString() ?? '',
        caseType: caseSelected ? CreateMessageDtoCaseType.DAMAGE : CreateMessageDtoCaseType.NONE,
        caseSource: caseSelected ? 'ICARE' : '',
        caseTitle: caseSelected?.incidentNumberFull ?? '',
        caseId: caseSelected?.id?.toString() ?? '',
        subject: formValue.subject ?? '',
        content: formValue.content ?? '',
        language: formValue.language ?? (this.translateService.currentLang || 'fr'),
        questionType: formValue.questionType ?? CreateMessageDtoQuestionType.GENERAL_QUESTION
      });

      const submission = await lastValueFrom(
        this.messageUploadService.createInternalMessageWithAttachments(
          dto,
          this.toFiles()
        )
      );
      const created = submission;

      const collaborator = this.selectedCollaborator();
      if (collaborator && created?.id) {
        const update = new UpdateMessageCollaboratorDto();
        update.assignedCollaboratorId = collaborator.id;
        update.assignedCollaboratorEmail = collaborator.email;
        await lastValueFrom(this.messageService.updateMessageCollaborator(created.id, update));
      }

      this.created.emit(created);
      this.notificationService.success(this.translateService.instant('message.feedback.createSuccess'));
      this.onClose();
    } catch (error) {
      this.notificationService.error(
        this.notificationService.errorMessage(error, this.translateService.instant('message.feedback.createError'))
      );
    } finally {
      this.submitting.set(false);
    }
  }

  onAttachmentsSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files?.length) {
      return;
    }

    const existingKeys = new Set(this.selectedAttachments().map(attachment => this.buildAttachmentKey(attachment.file)));
    const nextAttachments = [...this.selectedAttachments()];

    for (const file of Array.from(files)) {
      if (file.size === 0) {
        this.notificationService.warning(
          this.translateService.instant('message.attachments.feedback.emptyFile', { filename: file.name })
        );
        continue;
      }

      const fileKey = this.buildAttachmentKey(file);
      if (existingKeys.has(fileKey)) {
        this.notificationService.info(
          this.translateService.instant('message.attachments.feedback.duplicate', { filename: file.name })
        );
        continue;
      }

      existingKeys.add(fileKey);
      nextAttachments.push({
        id: fileKey,
        file,
        filename: file.name,
        contentType: file.type,
        size: file.size
      });
    }

    this.selectedAttachments.set(nextAttachments);
    if (input) {
      input.value = '';
    }
  }

  removeAttachment(attachmentId: string) {
    this.selectedAttachments.update(attachments => attachments.filter(attachment => attachment.id !== attachmentId));
  }

  protected formatAttachmentSize(size: number): string {
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    if (size < 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  protected getAttachmentTypeLabel(attachment: PendingAttachment): string {
    const extension = attachment.filename.split('.').pop()?.trim();
    if (extension) {
      return extension.toUpperCase();
    }
    if (attachment.contentType) {
      return attachment.contentType;
    }
    return this.translateService.instant('message.attachments.unknownType');
  }

  private toFiles(): File[] {
    return this.selectedAttachments().map(attachment => attachment.file);
  }

  private buildAttachmentKey(file: File): string {
    return [file.name, file.size, file.lastModified].join(NewMessageModalComponent.duplicateSeparator);
  }

  private getPersonEmail(person: IMyEcabPersonDto | null): string {
    if (!person?.communications) return '';
    const mail = person.communications.find(comm => comm.category === 'MAIL' && comm.principal)
      ?? person.communications.find(comm => comm.category === 'MAIL');
    return mail?.value ?? '';
  }

  private getPersonPhone(person: IMyEcabPersonDto | null): string {
    if (!person?.communications) return '';
    const preferred = person.communications.find(comm => comm.category === 'MOBILE' && comm.principal)
      ?? person.communications.find(comm => comm.category === 'MOBILE')
      ?? person.communications.find(comm => comm.category === 'TELEPHONE' && comm.principal)
      ?? person.communications.find(comm => comm.category === 'TELEPHONE');
    return preferred?.value ?? '';
  }

  private formatAddress(address: IMyEcabAddressDto): string {
    if (!address) return '';
    const streetName = address.street?.name ?? '';
    const localityZipCode = address.street?.locality?.zipCode ?? '';
    const localityName = address.street?.locality?.name ?? '';
    const num = `${address.streetNumber}${address.streetNumberSuffix ?? ''}`;
    return `${streetName} ${num}, ${localityZipCode} ${localityName}`.trim();
  }

  protected getBuildingOptionLabel(option: IMyEcabBuildingSummaryDto) {
    if (option.address) {
      return `${option.egidNumber ?? ''} - ${option.address.street?.name ?? ''} ${option.address.streetNumber ?? ''}${option.address.streetNumberSuffix ?? ''}, ${option.address.street?.locality?.zipCode ?? ''} ${option.address.street?.locality?.name ?? ''}`;
    }
    return '';
  }

  protected getDirectionOptions() {
    return [
      { value: CreateMessageDtoDirection.IN, label: this.translateService.instant('message.create.directionIn') },
      { value: CreateMessageDtoDirection.OUT, label: this.translateService.instant('message.create.directionOut') }
    ];
  }

  protected getChannelOptions() {
    return [
      { value: CreateMessageDtoChannel.PORTAL, label: this.translateService.instant('message.create.channel.portal') },
      { value: CreateMessageDtoChannel.MAIL, label: this.translateService.instant('message.create.channel.mail') },
      { value: CreateMessageDtoChannel.PHONE, label: this.translateService.instant('message.create.channel.phone') },
      { value: CreateMessageDtoChannel.LETTER, label: this.translateService.instant('message.create.channel.letter') }
    ];
  }

  protected getLanguageOptions() {
    return [
      { value: 'fr', label: 'FR' },
      { value: 'de', label: 'DE' }
    ];
  }

  protected getQuestionTypeOptions() {
    return [
      { value: CreateMessageDtoQuestionType.MY_ACCOUNT, label: this.translateService.instant('message.questionType.ACCOUNT') },
      { value: CreateMessageDtoQuestionType.GENERAL_QUESTION, label: this.translateService.instant('message.questionType.GENERAL') },
      { value: CreateMessageDtoQuestionType.CLAIM, label: this.translateService.instant('message.questionType.CLAIM') },
      { value: CreateMessageDtoQuestionType.PREVENTION, label: this.translateService.instant('message.questionType.PREVENTION') },
      { value: CreateMessageDtoQuestionType.ESTIMATION, label: this.translateService.instant('message.questionType.ESTIMATION') },
      { value: CreateMessageDtoQuestionType.BUILDINGS, label: this.translateService.instant('message.questionType.BUILDING') },
      { value: CreateMessageDtoQuestionType.SUBSIDIES, label: this.translateService.instant('message.questionType.SUBSIDY') }
    ];
  }

  protected getBuildingOptions() {
    const opts = (this.buildingOptions() || []).map(b => ({
      value: b,
      label: this.getBuildingOptionLabel(b)
    }));
    return [{ value: null, label: '---' }, ...opts];
  }

  protected getCaseOptions() {
    const opts = (this.caseOptions() || []).map(c => ({
      value: c,
      label: `${c.incidentNumberFull} ${c.eventType}`
    }));
    return [{ value: null, label: '---' }, ...opts];
  }

  onBuildingSelectChange(value: any) {
    this.onBuildingSelected(value);
  }

  onCaseSelectChange(value: any) {
    this.onCaseSelected(value);
  }
}
